import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import useWindowSize from 'hooks/useWindowSize';
import timedFetch from 'middleware/timedFetch';
import { type ErrorEventInfo, trackError } from 'helpers/ErrorUtils';
import { classifyUpstreamResult, isNetworkError } from 'helpers/upstreamDegradation';
import type { RequestSponsoredAdsBody, RequestSponsoredAdsBodyFilters, SponsoredAdsResponse, SponsoredAdsResults } from 'apis/sponsoredAds';
import useMartyContext from 'hooks/useMartyContext';
import { AD_PLACEMENT_COUNTS } from 'constants/sponsoredAds';
import { selectSponsoredAdsConfig } from 'selectors/environment';
import type { AppState } from 'types/app';
import { getViewportAsString } from 'utils/viewportUtil';
import { getAmazonSponsoredAds } from 'apis/amazonAds';
import type { AmazonAdsResponse } from 'apis/amazonAds';
import { trackOperation } from 'events/ops/opsEvent';

export const trackSponsoredAdImpression = (
  { url, fetcherName = 'SponsoredAdImpressions' }: { url: string; fetcherName?: string },
  fetcher = timedFetch
) => {
  const fetch = fetcher(fetcherName);
  fetch(url).catch((error: any) => trackError('ERROR', 'Failed to send Amazon Ads impression event', error));
};

export const trackSponsoredAdView = ({ url, fetcherName = 'SponsoredAdViews' }: { url: string; fetcherName?: string }, fetcher = timedFetch) => {
  const fetch = fetcher(fetcherName);
  fetch(url).catch((error: any) => trackError('ERROR', 'Failed to send Amazon Ads view event', error));
};

function concatIfExists(keyword = '', val?: any) {
  if (val) {
    //purposeful whitespace.
    return `${keyword} ${val}`;
  }
  return keyword;
}

// builds the keyword for search implementation based off filters.
// keyword should be "gender + most specific zc + brand[0] + color[0]"
export function buildKeywordFromFilters(filters: Partial<RequestSponsoredAdsBodyFilters>) {
  //find most specific zc- 'zc1', 'zc2', etc.
  const mostSpecificZcKey = Object.keys(filters).reduce((val, current) => {
    if (current.includes('zc')) {
      //ex: zc2 > zc1
      return current > val ? current : val;
    }

    return val;
  }, '') as keyof RequestSponsoredAdsBodyFilters;

  //all of these are optional on the filters object, if they exist add it to the keyword.
  let keyword = filters.txAttrFacet_Gender?.[0] || '';
  keyword = concatIfExists(keyword, filters[mostSpecificZcKey]?.[0]);
  keyword = concatIfExists(keyword, filters.brandNameFacet?.[0]);
  keyword = concatIfExists(keyword, filters.colorFacet?.[0]);
  return keyword;
}

//configure the payload for the get request to sponsoredAds endpoint
//handle the response and return the results.
export function fetchSponsoredAds(
  args: RequestSponsoredAdsBody,
  sponsoredAdsUrl: string,
  controller: AbortController
): Promise<SponsoredAdsResponse> {
  const body = {
    ...args
  };

  return getAmazonSponsoredAds({ body, endpointUrl: sponsoredAdsUrl, controller }).then((data: AmazonAdsResponse) => {
    const { results, impressionUrl } = data;

    return {
      AMAZON: {
        results,
        impressionUrl
      }
    };
  });
}

type UseSponsoredAds = Pick<RequestSponsoredAdsBody, 'pageType'> & Partial<Pick<RequestSponsoredAdsBody, 'keywords'>>;

//usage in PDP
type UseSponsoredAdsWithStockId = UseSponsoredAds & Required<Pick<RequestSponsoredAdsBody, 'stockId' | 'keywords'>>;

//usage in Search
type UseSponsoredAdsWithFilters = UseSponsoredAds &
  Required<Pick<RequestSponsoredAdsBody, 'filters'>> &
  Pick<RequestSponsoredAdsBody, 'placements' | 'term'> & { matchStyles?: string[] };

function isUseSponsoredAdsWithFilters(args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters): args is UseSponsoredAdsWithFilters {
  return (args as UseSponsoredAdsWithFilters).filters !== undefined;
}

interface SponsoredAdsFetchState {
  requestKey: string;
  ads?: SponsoredAdsResults;
  error: boolean;
}

export const useSponsoredAds = (
  args: UseSponsoredAdsWithStockId | UseSponsoredAdsWithFilters,
  isMarketingCurated = false,
  fetchAds = fetchSponsoredAds
) => {
  const [fetchState, setFetchState] = useState<SponsoredAdsFetchState>();

  const sessionId = useSelector((state: AppState) => state.cookies?.['session-id']);
  const { title: titleTag, canonical: url } = useSelector((state: AppState) => state.meta?.documentMeta) ?? {}; //documentMeta can be null
  const {
    marketplace: {
      search: { hasSponsoredAds },
      is6pm
    }
  } = useMartyContext();
  const sponsoredAdsUrl = useSelector((state: AppState) => selectSponsoredAdsConfig(state).url);
  const { width = 0 } = useWindowSize(200);
  const clientType = getViewportAsString(width);
  const placements = AD_PLACEMENT_COUNTS[clientType === 'mobile' ? 'reduced' : 'full'];

  const requestKey = JSON.stringify([
    sessionId,
    titleTag,
    url,
    sponsoredAdsUrl,
    clientType,
    width === 0,
    hasSponsoredAds,
    is6pm,
    isMarketingCurated,
    args
  ]);

  // require all pieces of information before fetching sponsoredAds.
  useEffect(() => {
    setFetchState(undefined);

    // early return if no marketplace support or missing required pieces from state that are async changes via other actions.
    if (!hasSponsoredAds || isMarketingCurated || !sessionId || !args.pageType || !titleTag || !url || !clientType || width === 0 || is6pm) {
      return;
    }

    // we need either or of these so if they dont exist, return;
    if (!(args as UseSponsoredAdsWithStockId).stockId && !(args as UseSponsoredAdsWithFilters).filters) {
      return;
    }

    const payload = {
      sessionId,
      titleTag,
      url,
      placements,
      clientType,
      ...args
    } as RequestSponsoredAdsBody;

    const isPlp = isUseSponsoredAdsWithFilters(args);
    if (isPlp) {
      payload.keywords = [buildKeywordFromFilters(args.filters)];
    }

    const controller = new AbortController();
    // A fetch can settle after abort, so both handlers must check this flag.
    let active = true;
    fetchAds(payload, sponsoredAdsUrl, controller)
      .then(
        data => {
          if (!active) {
            return;
          }
          if (data.AMAZON) {
            setFetchState({ requestKey, ads: data.AMAZON, error: false });
            if (isPlp && data.AMAZON.results) {
              trackOperation('SponsoredResultsLoad', 'success');
            }
          }
        },
        (e: unknown) => {
          if (!active) {
            return;
          }
          // Browser network errors and plain aborts do not count as load failures.
          const suppressError = isNetworkError(e) || classifyUpstreamResult(e) === 'cancelled';
          setFetchState({ requestKey, error: true });

          if (!suppressError) {
            trackError('ERROR', `[SponsoredResultsLoad Ops Event] Failed to load sponsored ads (getAds) on page ${url}`, e as ErrorEventInfo);
            if (isPlp) {
              trackOperation('SponsoredResultsLoad', 'failure');
            }
          }
        }
      )
      .catch((e: unknown) => {
        // Keep errors in these handlers separate from getAds failures.
        trackError('ERROR', `Failed to handle sponsored ads result on page ${url}`, e as ErrorEventInfo);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [requestKey]);

  // State can hold the old result until the effect clears it.
  if (fetchState?.requestKey !== requestKey || (!fetchState.ads && !fetchState.error)) {
    return undefined;
  }
  return { ...fetchState.ads, error: fetchState.error };
};

export const trackSponsoredAdsEvents = ({ url, fetcherName }: { url: string; fetcherName: string }, fetcher = timedFetch) => {
  const fetch = fetcher(fetcherName);
  fetch(url).catch((error: any) => trackError('ERROR', 'Failed to send Amazon Ads event', error));
};
